import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import Layout from '../components/Layout';
import { useI18n } from '../i18n';
import { useCan } from '../hooks/useMe';
import api from '../services/api';
import fmt from '../quotes/catalog/format';
import { downloadQuotePdf, quotePdfBase64 } from '../quotes/pdf/downloadQuote';
import offices from '../quotes/catalog/offices';

const RENEWAL_STYLE = { bg: '#F3E9F3', fg: '#531253' };
const NEW_STYLE = { bg: '#EDE6E3', fg: '#6B5F68' };

function Fact({ label, children }) {
  return (
    <Box sx={{ minWidth: 130 }}>
      <Typography sx={{
        fontSize: 10.5, fontWeight: 500, letterSpacing: '0.09em',
        textTransform: 'uppercase', color: '#847782', mb: 0.2,
      }}>{label}</Typography>
      <Box sx={{ fontSize: 14 }}>{children}</Box>
    </Box>
  );
}

export default function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, fmtDate } = useI18n();
  const canEdit = useCan('edit_quotes');
  const canSend = useCan('send_quotes');

  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  const load = useCallback(() => {
    api.get(`/api/quotes/${id}`)
      .then((res) => setQuote(res.data))
      .catch(() => setError(t('quote.loadError')));
  }, [id, t]);

  useEffect(() => { load(); }, [load]);

  // Sólo se busca cuenta cuando la cotización no tiene una ligada.
  useEffect(() => {
    if (!quote || quote.tenant_id) return;
    api.get(`/api/quotes/${id}/match`)
      .then((res) => setSuggestion(res.data))
      .catch(() => setSuggestion(null));
  }, [quote, id]);

  async function setRenewal(isRenewal) {
    setBusy(true);
    try {
      await api.patch(`/api/quotes/${id}`, { is_renewal: isRenewal });
      load();
    } catch {
      setError(t('quote.saveError'));
    } finally {
      setBusy(false);
    }
  }

  async function link(candidate) {
    setBusy(true);
    try {
      await api.post(`/api/quotes/${id}/link`, {
        tenant_id: candidate.tenant_id, source_db: candidate.source_db,
      });
      setSuggestion(null);
      load();
    } catch {
      setError(t('quote.saveError'));
    } finally {
      setBusy(false);
    }
  }

  async function download() {
    setBusy(true);
    await downloadQuotePdf(quote, { onError: () => setError(t('quote.pdfError')) });
    setBusy(false);
  }

  // El PDF se arma en el navegador y viaja en base64: el servidor ya no
  // guarda archivos temporales.
  async function send() {
    setBusy(true);
    setError(null);
    try {
      const base64 = await quotePdfBase64(quote);
      await api.post(`/api/quotes/${id}/send`, { email: quote.email, pdfBase64: base64 });
      setInfo(t('quote.sent'));
      load();
    } catch (err) {
      setError(err.response?.data?.error || t('quote.saveError'));
    } finally {
      setBusy(false);
    }
  }

  if (error && !quote) return <Layout><Alert severity="error">{error}</Alert></Layout>;
  if (!quote) return <Layout><Skeleton variant="rectangular" height={420} /></Layout>;

  const office = offices.find((o) => o.office_id === quote.office);

  return (
    <Layout>
      <Button variant="text" onClick={() => navigate('/quotes')} sx={{ mb: 1, color: '#531253' }}>
        {t('common.back')}
      </Button>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>{info}</Alert>}

      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="h5">{quote.company || '—'}</Typography>
          <Chip size="small" label={`#${quote.q_folio}`}
            sx={{ backgroundColor: '#EDE6E3', color: '#6B5F68', fontWeight: 600, fontSize: 11 }} />
          <Chip size="small"
            label={quote.is_renewal ? t('quote.isRenewal') : t('quote.newClient')}
            onClick={canEdit && !busy ? () => setRenewal(!quote.is_renewal) : undefined}
            sx={{
              backgroundColor: (quote.is_renewal ? RENEWAL_STYLE : NEW_STYLE).bg,
              color: (quote.is_renewal ? RENEWAL_STYLE : NEW_STYLE).fg,
              fontWeight: 600, fontSize: 10, letterSpacing: '0.06em',
              height: 20, borderRadius: '3px',
              cursor: canEdit ? 'pointer' : 'default',
            }} />
          <Box sx={{ flexGrow: 1 }} />
          {canEdit && (
            <Button size="small" variant="outlined" onClick={() => navigate(`/quotes/${id}/edit`)}
              sx={{ color: '#531253', borderColor: '#E4E0E2' }}>{t('common.edit') || 'Editar'}</Button>
          )}
          <Button size="small" variant="outlined" disabled={busy} onClick={download}
            sx={{ color: '#531253', borderColor: '#E4E0E2' }}>
            {t('quote.downloadPdf')}
          </Button>
          {canSend && (
            <Button size="small" variant="contained" disabled={busy || !quote.email} onClick={send}>
              {t('quote.send')}
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3.5, mt: 2 }}>
          <Fact label={t('quote.contact')}>{quote.contact_name || '—'}</Fact>
          <Fact label="Email">{quote.email || '—'}</Fact>
          <Fact label={t('quote.currency')}>{quote.currency}</Fact>
          <Fact label={t('quote.country')}>{quote.country || '—'}</Fact>
          <Fact label={t('quote.office')}>{office?.name || '—'}</Fact>
          <Fact label={t('quote.createdDate')}>{fmtDate(quote.createdAt)}</Fact>
          <Fact label={t('quote.doneBy')}>{quote.owner_name || '—'}</Fact>
          <Fact label={t('quote.validUntil')}>
            {quote.valid_until ? (
              <Box component="span" sx={{
                color: new Date(quote.valid_until) < new Date() ? '#AE5B61' : 'inherit',
                fontWeight: new Date(quote.valid_until) < new Date() ? 500 : 400,
              }}>
                {fmtDate(quote.valid_until)}
                {new Date(quote.valid_until) < new Date() && ` · ${t('quote.expired')}`}
              </Box>
            ) : '—'}
          </Fact>
          {quote.sent_at && (
            <Fact label={t('quote.send')}>{fmtDate(quote.sent_at)}</Fact>
          )}
        </Box>

        <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid #E4E0E2' }}>
          <Typography sx={{
            fontSize: 10.5, fontWeight: 500, letterSpacing: '0.09em',
            textTransform: 'uppercase', color: '#847782', mb: 0.6,
          }}>{t('quote.account')}</Typography>

          {quote.tenant_id ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button size="small" variant="text" sx={{ color: '#531253', p: 0, fontSize: 14 }}
                onClick={() => navigate(`/accounts/${quote.tenant_id}?source=${quote.source_db}`)}>
                {quote.account_name || quote.tenant_id}
              </Button>
              {quote.account_plan && (
                <Chip size="small" label={quote.account_plan}
                  sx={{ height: 18, fontSize: 9.5, fontWeight: 600, letterSpacing: '0.1em',
                        borderRadius: '3px', backgroundColor: '#EDE6E3', color: '#6B5F68' }} />
              )}
              {canEdit && (
                <Button size="small" variant="text" disabled={busy}
                  onClick={() => link({ tenant_id: null })}
                  sx={{ fontSize: 11.5, color: '#847782' }}>×</Button>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: 13.5, color: '#847782' }}>{t('quote.notLinked')}</Typography>
              {suggestion?.candidates?.slice(0, 4).map((c) => (
                <Button key={`${c.source_db}:${c.tenant_id}`} size="small" variant="outlined"
                  disabled={busy || !canEdit} onClick={() => link(c)}
                  sx={{ fontSize: 12, color: '#531253', borderColor: '#E4E0E2' }}>
                  {c.organization_name || c.tenant_id}
                </Button>
              ))}
              {suggestion && !suggestion.candidates && (
                <Typography sx={{ fontSize: 12, color: '#847782' }}>({suggestion.reason})</Typography>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.5fr 1fr' }, gap: 2 }}>
        <Paper sx={{ p: 2.2 }}>
          <Typography sx={{
            fontSize: 12, fontWeight: 600, letterSpacing: '0.08em',
            textTransform: 'uppercase', color: '#847782', mb: 1.5,
          }}>{t('quote.products')}</Typography>

          {(quote.products || []).map((p, i) => (
            <Box key={i} sx={{
              display: 'grid', gridTemplateColumns: '1fr 60px 120px', gap: 1.5,
              py: 1, borderBottom: '1px solid #F0ECEE', fontSize: 13.5, alignItems: 'center',
            }}>
              <Box>{p.name}</Box>
              <Box sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{p.quantity}</Box>
              <Box sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {fmt.getCurrencyFormat((p.quantity || 0) * (p.unit_cost || 0), quote.currency)}
              </Box>
            </Box>
          ))}

          <Box sx={{ display: 'grid', gap: 0.6, mt: 2, fontSize: 13.5 }}>
            {(() => {
              // discount y tax son porcentajes; el impuesto va sobre el
              // subtotal ya descontado, igual que en el servidor.
              const sub = quote.subTotal || 0;
              const descAmount = sub * ((quote.discount || 0) / 100);
              const impAmount = (sub - descAmount) * ((quote.tax || 0) / 100);
              return [
                [t('general.subtotal'), null, sub],
                [t('general.discount'), quote.discount, -descAmount],
                [t('general.taxes'), quote.tax, impAmount],
              ].map(([label, percent, value]) => (
                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', color: '#847782' }}>
                  <span>{label}{percent ? ` (${percent}%)` : ''}</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {fmt.getCurrencyFormat(value || 0, quote.currency)}
                  </span>
                </Box>
              ));
            })()}
            <Box sx={{
              display: 'flex', justifyContent: 'space-between', mt: 1, pt: 1,
              borderTop: '1px solid #E4E0E2', fontSize: 17, fontWeight: 600,
            }}>
              <span>{t('quote.total')}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {fmt.getCurrencyFormat(quote.total || 0, quote.currency)}
              </span>
            </Box>
          </Box>

          {quote.note && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #F0ECEE' }}>
              <Typography sx={{ fontSize: 11.5, color: '#847782' }}>{t('quote.note')}</Typography>
              <Typography sx={{ fontSize: 13.5 }}>{quote.note}</Typography>
            </Box>
          )}
        </Paper>

        <Paper sx={{ p: 2.2 }}>
          <Typography sx={{
            fontSize: 12, fontWeight: 600, letterSpacing: '0.08em',
            textTransform: 'uppercase', color: '#847782', mb: 1.5,
          }}>{t('accountDetail.timeline') || 'Historial'}</Typography>

          {(quote.events || []).map((e, i) => (
            <Box key={i} sx={{ py: 0.9, borderBottom: '1px solid #F0ECEE', fontSize: 13 }}>
              <Box sx={{ fontWeight: 500 }}>{e.action}{e.detail ? ` · ${e.detail}` : ''}</Box>
              <Box sx={{ fontSize: 11.5, color: '#847782' }}>
                {e.actor_name || '—'} · {fmtDate(e.created_at)}
              </Box>
            </Box>
          ))}
          {(!quote.events || quote.events.length === 0) && (
            <Typography sx={{ fontSize: 13.5, color: '#847782' }}>—</Typography>
          )}
        </Paper>
      </Box>
    </Layout>
  );
}
